import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

import { autoPublishErrors } from '@/app/scheduler/constants';
import {
  getIntervalMillisecondDuration,
  intersectIntervals,
  isAllTime,
  isWithinInterval,
} from '@/utils/dateUtils';
import { formatDateRangeLabel } from '@/utils/formatters';

dayjs.extend(minMax);

/**
 * Finds a timestamp overlap of media objects, if one exists
 *
 * @param {Object|Object[]} media one or more media objects
 * @returns {{start: Date, end: Date} | false} start and end times of the time range overlap, false if none exist
 */
export const findDateRangeOverlap = (media) => {
  const mediaList = Array.isArray(media) ? media : [media];

  const publishIntervals = mediaList
    .filter((m) => m?.canPublishWithin?.start && m?.canPublishWithin?.end)
    .map((m) => ({
      start: dayjs(m.canPublishWithin.start).toDate(),
      end: dayjs(m.canPublishWithin.end).toDate(),
    }));

  const intersection = intersectIntervals(...publishIntervals);
  return getIntervalMillisecondDuration(intersection) > 0 ? intersection : false;
};

/**
 * Resolve a post's media approved-publishing window, tolerating both shapes: library media exposes
 * `canPublishWithin` at the top level, while scheduled-post media nests it under
 * `meta.canPublishWithin`. Kept out of MediaModel so it stays confined to callers that opt in.
 *
 * @param {Object[]} media the post's media list
 * @returns {{ hasApprovedDates: boolean, overlap: {start: Date, end: Date} | false }}
 */
export const getApprovedPublishingWindow = (media) => {
  const normalized = (media ?? []).map((m) => ({
    canPublishWithin: m?.canPublishWithin ?? m?.meta?.canPublishWithin,
  }));
  const hasApprovedDates = normalized.some(
    (m) => m.canPublishWithin?.start && m.canPublishWithin?.end,
  );
  return { hasApprovedDates, overlap: hasApprovedDates ? findDateRangeOverlap(normalized) : false };
};

export const getConflictingMediaIds = (media, timestamp = dayjs()) =>
  []
    .concat(media)
    .filter((m) => m.canPublishWithin?.start > timestamp || m.canPublishWithin?.end < timestamp)
    .map((m) => m.id);

/**
 * Validate approved publishing dates of provided media have some overlap,
 * and the provided timestamp falls within the overlap. If validation
 * is successful, return null, otherwise, return an object specifying
 * an error banner.
 *
 * @param {{start: Date, end: Date} | false} overlap object containing start, and end dates
 * @param {Date} timestamp the timestamp to validate
 * @returns {{level: String, icon: String, message: String} | null} A validation message if invalid, or null if valid
 */
export const validatePublishDates = (overlap, timestamp) => {
  const dateTimestamp = timestamp && dayjs(timestamp).toDate();

  if (!overlap) {
    return {
      level: 'error',
      icon: 'date-blocked',
      code: autoPublishErrors.APPROVED_PUBLISHING_DATES_HAVE_NO_OVERLAP,
      message:
        'The selected media cannot be published together because their approved date ranges do not overlap. Please choose media with compatible date ranges or adjust your selection.',
    };
  }

  if (overlap.end < new Date()) {
    return {
      level: 'error',
      icon: 'date-blocked',
      message: 'Approved publishing dates for this media are in the past.',
    };
  }

  if (dateTimestamp && !isWithinInterval(dateTimestamp, overlap)) {
    const publishingDatesDescription = formatDateRangeLabel(overlap.start, overlap.end, {
      forceYearDisplay: false,
      withTime: true,
    });
    return {
      level: 'error',
      icon: 'date-blocked',
      code: 'OUTSIDE_OF_APPROVED_PUBLISHING_DATES',
      message: `Media must be published between ${publishingDatesDescription}`,
    };
  }

  return null;
};

/**
 * Validate approved publishing dates of provided media have some overlap,
 * and the provided timestamp falls within the overlap. If validation
 * is successful, return null, otherwise, return an object specifying
 * an error banner.
 *
 * @param {{start: Date, end: Date} | false} overlap object containing start, and end dates
 * @param {Date} timestamp the currently selected post timestamp
 * @returns {{level: String, message: String} | null} A warning message if applicable, otherwise null
 */
export const getPublishDateWarning = (overlap, timestamp) => {
  const dateTimestamp = timestamp && dayjs(timestamp).toDate();

  if (
    !overlap ||
    isAllTime(overlap) ||
    (dateTimestamp && !isWithinInterval(dateTimestamp, overlap))
  ) {
    return null;
  }

  const { start, end } = overlap;
  const approvedPublishDates = formatDateRangeLabel(start, end, {
    forceYearDisplay: false,
    withTime: true,
  });

  return {
    level: 'information',
    message: `Media must be published between ${approvedPublishDates}`,
  };
};

/**
 * Builds a Select Media drawer `validateMedia(media, { selection })` rule that
 * disables media whose approved publishing window (content rights) is
 * incompatible with the scheduled time or the rest of the selection. Returns a
 * disable-reason string, or undefined when the media is fine. No-ops for media
 * without a `canPublishWithin` window (so it's safe before media/v2 returns it).
 *
 * @param {Object} options
 * @param {Date|string|import('dayjs').Dayjs} [options.scheduledTime] post's scheduled timestamp
 * @param {boolean} [options.disallowPastPublishDates]
 * @param {boolean} [options.disallowIncompatiblePublishDates]
 * @returns {(media: Object, ctx?: { selection?: Object[] }) => (string | undefined)}
 */
export const createPublishDateMediaValidator =
  ({
    scheduledTime,
    disallowPastPublishDates = false,
    disallowIncompatiblePublishDates = false,
  } = {}) =>
  (media, { selection = [] } = {}) => {
    const range = media?.canPublishWithin;
    if (!range?.start || !range?.end) return undefined;

    const window = { start: dayjs(range.start).toDate(), end: dayjs(range.end).toDate() };

    if (disallowPastPublishDates && window.end < new Date()) {
      return 'The approved publishing dates are in the past';
    }

    if (scheduledTime && !isWithinInterval(dayjs(scheduledTime).toDate(), window)) {
      const label = formatDateRangeLabel(window.start, window.end, {
        forceYearDisplay: false,
        withTime: true,
      });
      return `This item must be published between ${label}`;
    }

    if (disallowIncompatiblePublishDates) {
      const others = selection.filter((item) => item?.id !== media?.id);
      // Only blame this media if the rest of the selection already shares a
      // window but adding this media breaks the overlap.
      if (
        others.length &&
        findDateRangeOverlap(others) &&
        !findDateRangeOverlap([...others, media])
      ) {
        const verb = others.length === 1 ? 'has' : 'have';
        return `Media selected ${verb} incompatible approved publishing dates with this item`;
      }
    }

    return undefined;
  };
